import React, { useEffect, useState } from 'react';
import { Box, Chip, LinearProgress, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppraisalService } from '../../services/appraisal.service';
import { DepartmentService } from '../../services/department.service';
import { useToast } from '../../context/ToastContext';
import { Department } from '../../interfaces/department.interface';
import { ICycleDetail, IReviewSummary, REVIEW_STATUSES, ReviewStatus } from '../../interfaces/appraisal.interface';
import { reviewStatusColor } from './bands';
import ReviewTable from './ReviewTable';

interface Props {
    institutionId: string;
    cycle: ICycleDetail;
}

/** Every review in a started cycle, with status and department filters and progress counts. */
const CycleOverview: React.FC<Props> = ({ institutionId, cycle }) => {
    const toast = useToast();
    const { t } = useTranslation('appraisal');
    const [status, setStatus] = useState<ReviewStatus | ''>('');
    const [departmentId, setDepartmentId] = useState('');
    const [departments, setDepartments] = useState<Department[]>([]);
    const [reviews, setReviews] = useState<IReviewSummary[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await DepartmentService.getDepartmentsByInstitution();
                setDepartments(res?.content ?? []);
            } catch (err) {
                toast.showApiError(err, t('participants.addDialog.errors.departments'));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setLoading(true);
        (async () => {
            try {
                setReviews(await AppraisalService.cycleReviews(institutionId, cycle.id, {
                    status: status || undefined,
                    departmentId: departmentId || undefined,
                }));
            } catch (err) {
                toast.showApiError(err, t('errors.loadReviews'));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, cycle.id, status, departmentId]);

    const percent = cycle.participantCount > 0 ? Math.round((cycle.completedCount / cycle.participantCount) * 100) : 0;
    const counts = REVIEW_STATUSES.map(s => ({ status: s, count: reviews.filter(r => r.status === s).length }));

    return (
        <Box>
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography>
                    {t('overview.progress', { completed: cycle.completedCount, total: cycle.participantCount })}
                </Typography>
                <LinearProgress variant="determinate" value={percent} sx={{ my: 1, height: 8, borderRadius: 4 }} />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {counts.map(c => (
                        <Chip key={c.status} size="small" color={reviewStatusColor[c.status]}
                            label={t('overview.statusCount', { label: t(`reviewStatus.${c.status}`), count: c.count })} />
                    ))}
                </Box>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField select size="small" label={t('overview.filterStatus')} value={status}
                    onChange={e => setStatus(e.target.value as ReviewStatus | '')} sx={{ minWidth: 220 }}>
                    <MenuItem value="">{t('overview.all')}</MenuItem>
                    {REVIEW_STATUSES.map(s => <MenuItem key={s} value={s}>{t(`reviewStatus.${s}`)}</MenuItem>)}
                </TextField>
                <TextField select size="small" label={t('overview.filterDepartment')} value={departmentId}
                    onChange={e => setDepartmentId(e.target.value)} sx={{ minWidth: 220 }}>
                    <MenuItem value="">{t('overview.all')}</MenuItem>
                    {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </TextField>
            </Box>

            <ReviewTable reviews={reviews} loading={loading} mode="admin" emptyText={t('overview.empty')} />
        </Box>
    );
};

export default CycleOverview;
